import axios from "axios";
import { appConfig } from "./AppConfig";
import { appCertification } from "./AppCertification";
import { dateTimeHelper } from "./DateTimeHelper";
import { v4 as uuidv4 } from "uuid";
import qs from "qs";
import { commonFunction } from "./CommonFunction";
import { getCsrfTokenFromCookie } from "./CsrfUtil";
import { AuthError } from "./AuthError";
import { messsageUtil } from "./MesssageUtil";
import { i18n } from "@/lang/lang.js";

/**
 * axiosとCognitoアクセスをラップした専用クラスです。
 */
class HttpClient {
  /**
   * コンストラクタ
   */
  constructor() {
    axios.defaults.timeout = 60000;
    axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
    axios.defaults.baseURL = appConfig.APP_CONFIG.BASE_URL;
    axios.defaults.withCredentials = true;
    // /auth/get-id-token から取得した idToken のメモリキャッシュと有効期限（epoch ミリ秒）。
    this.idTokenCache = null;
    this.idTokenExpiry = 0;
  }

  // idToken キャッシュの再利用許容マージン（秒）。期限のこの秒数前から再取得する。
  ID_TOKEN_REFRESH_MARGIN_SEC = 120;

  // バージョンエラーステータスリスト
  versionErrorStatusList = [
    // 最新バージョンのPC画面利用不可
    "LatestVersionPCScreenUnavailable",
    // 最新バージョンのPC画面リリース中
    "LatestVersionPCScreenReleasing",
    // PC画面のバージョン不一致
    "PCScreenVersionMismatch",
    // 最新バージョンのAPI利用不可
    "LatestVersionAPIUnavailable",
    // 最新バージョンのAPIリリース中
    "LatestVersionAPIReleasing",
    // APIのバージョン不一致
    "APIVersionMismatch",
  ];

  // MEMO 外部ライブラリで頻繁に使用され、
  // 不要な解決がされる可能性が高いため、configという変数は避けてください。

  /**
   * HTTPメソッド定数定義
   */
  Method = {
    /**
     * GET
     */
    GET: "GET",
    /**
     * POST
     */
    POST: "POST",
  };

  /**
   * URLの末尾にあるスラッシュを削除します。
   * @param {String} url
   */
  removeUrlEndSlash(url) {
    return url.replace(/\/$/, "");
  }

  /**
   * dev(localhost) では絶対 URL を同一オリジンの相対パスへ変換し、Vite dev プロキシ経由
   * （同一オリジン）にして CORS / 資格情報付きワイルドカード問題を回避する。
   * BASE_URL（AppCertification.js）と同じ方針。本番・ステージングでは絶対 URL のまま返す。
   * @param {String} absoluteUrl 変換対象の絶対 URL
   */
  toDevRelativeUrl(absoluteUrl) {
    const host = window.location.hostname;
    if (host !== "localhost" && host !== "127.0.0.1") return absoluteUrl;
    const url = new URL(absoluteUrl);
    return url.pathname + url.search;
  }

  /**
   * 有効な idToken を返します。
   * サーバ側 /auth/get-id-token Lambda（HttpOnly Cookie の refresh_token を使用）から
   * idToken を取得します。取得した値はメモリにキャッシュし、有効期限（expiresIn）まで再利用します。
   * 期限が近い（ID_TOKEN_REFRESH_MARGIN_SEC 以内）場合は再取得します。
   * @returns {Promise<String>} idToken(JWT)
   */
  async getIdToken() {
    // キャッシュが有効期限内（マージン込み）なら再利用してLambda呼び出しを省く。
    const now = Date.now();
    if (this.idTokenCache && now < this.idTokenExpiry) {
      return this.idTokenCache;
    }

    const apiUrl = "/auth/get-id-token";
    // 認証は Cookie(refresh_token) で行われ、ボディは Lambda 側で未使用。
    const result = await this.lambdaUrlPostAsync(apiUrl, {});
    // 非2xx は undefined、想定内エラーは { ok:false, code } で返る。
    if (!result || !result.idToken) {
      await this.logout();
      // ログイン画面へ遷移
      alert(i18n.t("message.P-COM-001_003_W"));
      window.location.href = "/";
    }

    this.idTokenCache = result.idToken;
    // expiresIn(秒) からマージンを引いた時刻を期限とする。未指定時は1時間とみなす。
    const expiresInSec = result.expiresIn ?? 3600;
    const validSec = Math.max(expiresInSec - this.ID_TOKEN_REFRESH_MARGIN_SEC, 0);
    this.idTokenExpiry = now + validSec * 1000;
    return this.idTokenCache;
  }

  /**
   * 現時点で有効なヘッダーを設定します。
   * @param {AxiosRequestConfig} _config
   */
  setHeader(_config) {
    return new Promise((resolve, reject) => {
      if (_config === undefined) {
        _config = {};
      }

      // axios問題で配列クエリパラメータのシリアライズフォーマット修正
      const paramsSerializer = (params) =>
        qs.stringify(params, { arrayFormat: "indices", allowDots: true });
      _config.paramsSerializer = paramsSerializer;

      // APIアクセスであれば常にトークンを更新して設定します。
      if (
        _config.baseURL == undefined ||
        _config.baseURL == appConfig.APP_CONFIG.BASE_URL ||
        (axios.defaults.baseURL == appConfig.APP_CONFIG.BASE_URL && _config.baseURL == undefined)
      ) {
        this.getIdToken()
          .then((idToken) => {
            _config.headers = { Authorization: idToken };
            resolve(_config);
          })
          .catch((error) => {
            console.error("setHeader error", error);
            reject(_config);
          });
      } else {
        resolve(_config);
      }
    });
  }

  /**
   * axios getのラッパーメソッドです。
   * @param {String} uri
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<AxiosResponse<any>>}
   */
  async get(uri, _config) {
    const newConfig = await this.setHeader(_config);
    return axios.get(uri, newConfig);
  }

  /**
   * ログアウトします。
   * 認証は Cookie ベースのため、サーバ側 Cookie の失効（setCookieExpired）と
   * sessionStorage の認証情報クリア（clearAuthSession）で完結する。
   */
  async logout() {
    // ログインユーザが取得したロックを破棄する
    // ロックの破棄とログアウト処理を並列実行
    try {
      commonFunction.exclusiveReserve(void 0, appConfig.EXCLUSIVE_RESERVE_PROC_DIV.DESTROY, "", "");
    } catch (ex) {
      console.error(ex);
    }
    this.clearAuthSession();
    // 認証に関わる Cookie 情報を期限切れに設定します。
    await this.setCookieExpired();
    console.log("logged out");
  }

  /**
   * 認証関連の sessionStorage エントリをクリアします。
   * 業務属性（comp_sid, sales_office_sid 等）は別タスクのスコープのため残します。
   */
  clearAuthSession() {
    const authKeys = [
      // ログインユーザID。ログアウト時はクリアする（旧 cognito.clearToken と同挙動）。
      // セッション中は reqComExecUser 等で参照するため、本メソッドはログアウト時のみ呼ぶ。
      "usercode",
      "userps",
      "idToken",
      "idToken.payload.exp",
      "refreshToken",
      "accessToken",
      "accessToken.payload.exp",
      "accessToken.payload.iat",
      "updateToken",
      "credentials.accessKeyId",
      "credentials.secretAccessKey",
      "credentials.sessionToken",
      "COGNITO.AWS_ACCESS_KEY_ID",
      "COGNITO.AWS_SECRET_ACCESS_KEY",
      "COGNITO.IDENTITY_POOL_ID",
      "COGNITO.REGION",
    ];
    for (const key of authKeys) {
      sessionStorage.removeItem(key);
    }
    // メモリ上の idToken キャッシュも破棄する。
    this.idTokenCache = null;
    this.idTokenExpiry = 0;
  }

  /**
   * GetAPIリクエスト用のConfigを作成します。
   * @returns {AxiosRequestConfig}
   */
  createGetApiRequestConfig() {
    const _config = {};
    //const language = window.navigator.language;
    //const lang = language.toLowerCase().split("-");

    _config.params = {
      // reqComLanguageCd: lang[0],
      reqComCompanySid: sessionStorage.getItem("comp_sid"),
      //reqComCompanySid: "2100000100",
      reqComOfficeSid: sessionStorage.getItem("sales_office_sid"),
      reqComDeviceImei: "WEB", //ブランク
      reqComAppVersion: appCertification.APPVERSION, //ブランク
      reqComExecUser: sessionStorage.getItem("usercode"),
      reqComExecTimestamp: dateTimeHelper.convertUTC(),
      reqComRequestId: uuidv4(),
      reqComPaginationFlg: "0",
      reqComPageIndex: "1",
      reqComPageLimit: "100",
      reqComLanguageCd: sessionStorage.getItem("lang"),
      reqComReferenceDate: dateTimeHelper.convertUTC(),
      reqComSortItem: "",
      reqComTimeZone: dateTimeHelper.getTimeZone(),
    };

    return _config;
  }

  /**
   * PostAPIリクエスト用のConfigを作成します。
   * @returns
   */
  createRequestBodyConfig() {
    const body = {};
    let kvp = {};
    kvp.reqComComponentId = "";
    kvp.reqComCompanySid = sessionStorage.getItem("comp_sid");
    kvp.reqComOfficeSid = sessionStorage.getItem("sales_office_sid");
    kvp.reqComDeviceImei = "WEB";
    kvp.reqComAppVersion = appCertification.APPVERSION;
    kvp.reqComExecUser = sessionStorage.getItem("usercode");
    kvp.reqComExecTimestamp = dateTimeHelper.convertUTC();
    kvp.reqComRequestId = uuidv4();
    kvp.reqComLanguageCd = sessionStorage.getItem("lang");
    kvp.reqComTimeZone = dateTimeHelper.getTimeZone();

    body.reqCom = kvp;
    body.reqIdv = {};
    return body;
  }

  /**
   * API GatewayのGetリクエストです。
   * @param {String} uri
   * @param {any} data
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<R>}
   */
  async secureGet(uri, _config) {
    const newConfig = await this.setHeader(_config);
    return axios
      .get(uri, newConfig)
      .then((response) => {
        // ログ出力
        // console.info("response", response);
        // バージョンエラーステータスリスト
        const versionErrorStatusList = [
          // 最新バージョンのPC画面利用不可
          "LatestVersionPCScreenUnavailable",
          // 最新バージョンのPC画面リリース中
          "LatestVersionPCScreenReleasing",
          // PC画面のバージョン不一致
          "PCScreenVersionMismatch",
          // 最新バージョンのAPI利用不可
          "LatestVersionAPIUnavailable",
          // 最新バージョンのAPIリリース中
          "LatestVersionAPIReleasing",
          // APIのバージョン不一致
          "APIVersionMismatch",
        ];

        // レスポンスステータス
        const resComStatus = response.data.resCom.resComStatus;
        // バージョンエラーに該当する場合
        if (versionErrorStatusList.includes(resComStatus)) {
          // ログアウトする
          this.logout();
          // ログ出力
          // console.info("logout");
          // エラーメッセージ表示
          alert(response.data.resCom.resComMessage);
          // PC画面のバージョン不一致の場合
          if (resComStatus === "PCScreenVersionMismatch") {
            // キャッシュをクリアする
            window.location.reload();
          }
          // ログイン画面へ遷移
          window.location.href = "/";
        }
        // responseを返す
        return response;
      })
      .catch((ex) => {
        console.error(ex);
        throw ex;
      });
  }

  /**
   * API GatewayのPostリクエストです。
   * @param {String} uri
   * @param {any} data
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<R>}
   */
  async securePost(uri, data, _config) {
    const newConfig = await this.setHeader(_config);
    return axios
      .post(uri, data, newConfig)
      .then((response) => {
        // ログ出力
        // console.info("response", response);
        // バージョンエラーステータスリスト
        const versionErrorStatusList = [
          // 最新バージョンのPC画面利用不可
          "LatestVersionPCScreenUnavailable",
          // 最新バージョンのPC画面リリース中
          "LatestVersionPCScreenReleasing",
          // PC画面のバージョン不一致
          "PCScreenVersionMismatch",
          // 最新バージョンのAPI利用不可
          "LatestVersionAPIUnavailable",
          // 最新バージョンのAPIリリース中
          "LatestVersionAPIReleasing",
          // APIのバージョン不一致
          "APIVersionMismatch",
        ];

        // レスポンスステータス
        const resComStatus = response.data.resCom.resComStatus;
        // バージョンエラーに該当する場合
        if (versionErrorStatusList.includes(resComStatus)) {
          // ログアウトする
          this.logout();
          // ログ出力
          // console.info("logout");
          // エラーメッセージ表示
          alert(response.data.resCom.resComMessage);
          // PC画面のバージョン不一致の場合
          if (resComStatus === "PCScreenVersionMismatch") {
            // キャッシュをクリアする
            window.location.reload();
          }
          // ログイン画面へ遷移
          window.location.href = "/";
        }
        // responseを返す
        return response;
      })
      .catch((ex) => {
        console.error(ex);
        throw ex;
      });
  }

  /**
   * 共通レスポンスエラーをチェックします。
   * - バージョンエラー: ログアウトしてログイン画面へ遷移します。
   * ※ 認可エラー（HTTP 403）は secureGet/securePost の catch 節で
   *   handleAuthorizationError により処理します。
   */
  handleResponseError(response) {
    this.handleVersionError(response);
    return response;
  }

  /**
   * バージョンエラーのレスポンスをチェックし、該当する場合はログアウトします。
   */
  handleVersionError(response) {
    const resComStatus = response.data.resCom?.resComStatus;
    if (this.versionErrorStatusList.includes(resComStatus)) {
      this.logout();
      alert(response.data.resCom.resComMessage);
      if (resComStatus === "PCScreenVersionMismatch") {
        window.location.reload();
      }
      window.location.href = "/";
    }
    return response;
  }

  /**
   * 認可エラー（HTTP 403）をチェックします。
   * 業務ロジック (Spring Boot) が CSRF 二重送信検証や所属営業所(officeSid)照合などに
   * 失敗した場合に HTTP 403 を返します。共通層でエラーダイアログを表示し、AuthError を
   * 送出して後続の画面処理（および二重ダイアログ表示）を中断します。
   * ※ エラーメッセージ・挙動の詳細は今後再検討する（暫定でシステムエラーメッセージを表示）。
   */
  handleAuthorizationError(error) {
    if (error?.response?.status !== 403) {
      return;
    }
    const message = messsageUtil.getMessage("P-999-999_999_E");
    alert(message);
    // 後続の画面処理（および二重ダイアログ表示）を中断するため例外を送出する
    throw new AuthError("AuthorizationError", message);
  }

  /**
   * API Gateway 30秒タイムアウトを回避するための Step Functions 統合処理の GETバージョンです。
   * @param path Step Functions を経由してAPIサーバに渡すURLのパス
   * @param params Step Functions を経由してAPIサーバに渡す（queryString に変換する前の）パラメータ
   * @returns Step Functions を経由して実行したAPIのレスポンス
   */
  async stepFunctionsGetAsync(path, params) {
    try {
      const pathPattern = path.split("/")[0];
      const apiUrl =
        pathPattern === "reporting"
          ? sessionStorage.getItem("AWS.REPORTINGAPIURL")
          : pathPattern === "business-g3"
          ? sessionStorage.getItem("AWS.APIG3URL")
          : sessionStorage.getItem("AWS.APIURL");

      const paramString = new URLSearchParams(params).toString();

      const inputValue = {
        apiUrl: apiUrl + path + "?" + paramString,
        body: "",
        method: this.Method.GET,
      };

      return await this.stepFunctionsFetchAsync(path, inputValue);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * API Gateway 30秒タイムアウトを回避するための Step Functions 統合処理の POSTバージョンです。
   * @param path Step Functions を経由してAPIサーバに渡すURLのパス
   * @param body Step Functions を経由してAPIサーバに渡す Body
   * @param skipResult 非同期処理を待たない用のフラグ
   * - true：非同期処理を待たない
   * - false：非同期処理を待つ（デフォルト）
   * @returns Step Functions を経由して実行したAPIのレスポンス
   */
  async stepFunctionsPostAsync(path, body, skipResult = false) {
    try {
      const pathPattern = path.split("/")[0];
      const apiUrl =
        pathPattern === "reporting"
          ? sessionStorage.getItem("AWS.REPORTINGAPIURL")
          : pathPattern === "business-g3"
          ? sessionStorage.getItem("AWS.APIG3URL")
          : sessionStorage.getItem("AWS.APIURL");

      const inputValue = {
        apiUrl: apiUrl + path,
        body: body,
        method: this.Method.POST,
      };

      return await this.stepFunctionsFetchAsync(path, inputValue, skipResult);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * @param path Step Functions を経由してAPIサーバに渡すURLのパス
   * @param inputValue Step Functions を経由してAPIサーバに渡すインプット
   * @param skipResult 非同期処理を待たない用のフラグ
   * - true：非同期処理を待たない
   * - false：非同期処理を待つ（デフォルト）
   * @returns Step Functions を経由して実行したAPIのレスポンス
   */
  async stepFunctionsFetchAsync(path, inputValue, skipResult = false) {
    const startExecutionUrl = this.toDevRelativeUrl(
      sessionStorage.getItem("AWS.STARTEXECUTIONURL")
    );
    const stateMachineArn = sessionStorage.getItem("AWS.STATEMACHINEARN");
    const dateKey = dateTimeHelper.convertUTC().replace(/[^0-9]/g, "");
    const executionName = dateKey + "_" + path.replace(/[^0-9a-zA-Z]/g, "");

    const requestValue = {
      input: JSON.stringify(inputValue),
      name: executionName.length > 80 ? executionName.substring(0, 80) : executionName,
      stateMachineArn: stateMachineArn,
    };

    const executionArn = await this.startExecutionAsync(startExecutionUrl, requestValue);

    const describeExecutionUrl = this.toDevRelativeUrl(
      sessionStorage.getItem("AWS.STARTEXECUTIONURL") + "/status"
    );
    const delay = 2000;
    let response = null;

    if (skipResult) {
      return;
    }

    do {
      await new Promise((resolve) => setTimeout(resolve, delay));
      response = await this.describeExecutionAsync(executionArn, describeExecutionUrl);
    } while (response && response.status === "RUNNING");

    if (response.status.toUpperCase() !== "SUCCEEDED") {
      switch (response.error) {
        case appConfig.STEP_FUNCTIONS_STATES_ERRORS.DATA_LIMIT_EXCEEDED:
          throw new Error(i18n.t("error.STEP_FUNCTIONS_STATES_ERRORS_DATA_LIMIT_EXCEEDED"));
        case appConfig.STEP_FUNCTIONS_STATES_ERRORS.TIMEOUT:
          throw new Error(i18n.t("error.STEP_FUNCTIONS_STATES_ERRORS_TIMEOUT"));
        default:
          throw new Error(i18n.t("error.err_asyncProcess"));
      }
    }

    return response.output;
  }

  /**
   * API Gateway のアクション：startExecution をコールします。
   * @param startExecutionUrl startExecution の URL
   * @param requestValue Step Functions に渡すインプット
   * @returns startExecution の Arn
   */
  async startExecutionAsync(startExecutionUrl, requestValue) {
    const idToken = await this.getIdToken();

    const response = await fetch(startExecutionUrl, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestValue),
    });

    if (!response.ok) {
      // TODO: エラーメッセージの定義等のエラーハンドリング
      throw new Error(i18n.t("error.err_asyncProcess"));
    }

    const jsonData = await response.json();

    return jsonData.executionArn;
  }

  /**
   * API Gateway のアクション：describeExecution をコールして非同期処理の進捗状況を確認します。
   * @param executionArn する 進捗を確認する非同期処理の Arn
   * @param describeExecutionUrl describeExecution の URL
   * @returns 非同期処理の進捗状況および実行結果
   */
  async describeExecutionAsync(executionArn, describeExecutionUrl) {
    const idToken = await this.getIdToken();

    const body = {
      executionArn: executionArn,
    };

    const response = await fetch(describeExecutionUrl, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return await response.json();
  }

  /**
   * S3 PutObject 用の署名付きURLを取得します。返却される値は以下の形式の Json になります。
   *   body.presignedUrl;
   *   body.fileKey;
   */
  async getPutObjectSignedUrl() {
    try {
      const idToken = await this.getIdToken();
      const path = "/business/a-issue-presigned-url";
      const apiUrl = path.startsWith("/") ? path : "/" + path;
      const baseUrl = this.removeUrlEndSlash(appConfig.APP_CONFIG.BASE_URL);
      const url = baseUrl + apiUrl;
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: { Authorization: idToken },
      });
      const jsonData = await response.json();
      return JSON.parse(jsonData.body);
    } catch (ex) {
      console.log(ex);
    }
  }

  /**
   * ユーザーIDとパスワードでCognito認証した結果をCookieに記録するAPIをコールします。
   * @param userId
   * @param password
   * @param {String} [shipperKbn] 荷主区分（"1"=荷主 / "0"=営業所）。サーバ側で使用する
   *   Cognito プール（ClientId / UserPoolId）の切り替えに使う。未指定時は sessionStorage
   *   の shipperKbn を参照し、それも無ければ "0"（営業所）。
   * @returns APIの実行結果
   */
  async initiateAuth(userId, password, shipperKbn) {
    try {
      const apiUrl = "/auth/initiate";
      const kbn = shipperKbn ?? sessionStorage.getItem("shipperKbn") ?? "0";
      const requestData = { userName: userId, password: password, shipperKbn: kbn };
      return await this.lambdaUrlPostAsync(apiUrl, requestData);
    } catch (error) {
      console.error(error);
      throw new AuthError(error?.code ?? "UnknownError", error?.message);
    }
  }

  /**
   * HttpOnly 属性の Cookie を無効（期限切れ）に設定します。
   */
  async setCookieExpired() {
    try {
      const apiUrl = "/auth/set-cookie-expired";
      const requestData = { mode: "all" };
      await this.lambdaUrlPostAsync(apiUrl, requestData);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * ユーザーのパスワードを変更します。
   * サーバ側 /auth/change-password Lambda（Cognito AdminSetUserPassword）を呼び出します。
   * 自分自身の変更時はサーバが新パスワードで再ログインし、新しい認証 Cookie を再発行します。
   * @param {String} userId 変更対象ユーザID
   * @param {String} oldPassword 現行パスワード（自分自身の変更検証時のみ使用）
   * @param {String} newPassword 新パスワード
   * @returns {Promise<any>} APIの実行結果（{ ok:true, ... }）
   */
  async changePassword(userId, oldPassword, newPassword) {
    const apiUrl = "/auth/change-password";
    const requestData = { userId, oldPassword, newPassword };
    const result = await this.lambdaUrlPostAsync(apiUrl, requestData);
    // 非2xx（401/403/500 等）は undefined、Cognito 想定内エラーは { ok:false, code } で返る。
    if (!result || result.ok !== true) {
      throw new AuthError(result?.code ?? "UnknownError");
    }
    return result;
  }

  /**
   * ユーザーを登録します（upsert）。
   * サーバ側 /auth/admin/create-user Lambda（Cognito ユーザ作成/属性更新/永続パスワード設定）
   * を呼び出します。対象が未存在なら作成、存在すれば氏名更新し、いずれもパスワードを設定します。
   * @param {String} userId 登録対象ユーザID
   * @param {String} password 設定する永続パスワード
   * @param {String} familyName 姓
   * @param {String} givenName 名
   * @returns {Promise<any>} APIの実行結果（{ ok:true, created:boolean }）
   */
  async createCognitoUser(userId, password, familyName, givenName) {
    const apiUrl = "/auth/create-cognito-user";
    const requestData = { userId, password, familyName, givenName };
    const result = await this.lambdaUrlPostAsync(apiUrl, requestData);
    // 非2xx（401/403/500 等）は undefined、Cognito 想定内エラーは { ok:false, code } で返る。
    if (!result || result.ok !== true) {
      throw new AuthError(result?.code ?? "UnknownError");
    }
    return result;
  }

  /**
   * ユーザーの氏名属性を変更します。
   * サーバ側 /auth/admin/update-attributes Lambda（Cognito AdminUpdateUserAttributes）
   * を呼び出します。
   * @param {String} userId 変更対象ユーザID
   * @param {String} familyName 姓
   * @param {String} givenName 名
   * @returns {Promise<any>} APIの実行結果（{ ok:true }）
   */
  async updateCognitoUserAttributes(userId, familyName, givenName) {
    const apiUrl = "/auth/update-cognito-user-attributes";
    const requestData = { userId, familyName, givenName };
    const result = await this.lambdaUrlPostAsync(apiUrl, requestData);
    // 非2xx（401/403/500 等）は undefined、Cognito 想定内エラーは { ok:false, code } で返る。
    if (!result || result.ok !== true) {
      throw new AuthError(result?.code ?? "UnknownError");
    }
    return result;
  }

  /**
   * Lambda Functions URL へのPOSTリクエストを実行して結果を返します。
   * @param path APIの相対パス
   * @param body Body
   * @param {Object} options - オプション
   * @param {boolean} options.skipCsrf - true の場合 CSRF トークンを送信しない（/auth/initiate 用）
   * @returns APIの実行結果
   */
  async lambdaUrlPostAsync(path, body, options = {}) {
    if (!path || path.length < 1) return null;

    //const baseUrl = "https://fg-cirrus.nikkon-is.com";
    const baseUrl = this.removeUrlEndSlash(appConfig.APP_CONFIG.BASE_URL);
    const apiUrl = path.startsWith("/") ? path : "/" + path;
    const url = baseUrl + apiUrl;
    const bodyData = JSON.stringify(body);
    const bodyDataHash = await this.getSha256HashValue(bodyData);
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("x-amz-content-sha256", bodyDataHash);

    if (!options.skipCsrf) {
      headers.append("X-CSRF-Token", getCsrfTokenFromCookie());
    }

    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: headers,
      body: bodyData,
    });

    if (!response.ok) return undefined;

    return await response.json();
  }

  /**
   * 渡された文字列データのSHA256ハッシュ値を取得して返します。
   * @param data ハッシュ値の計算対象
   * @returns SHA256ハッシュ値
   */
  async getSha256HashValue(data) {
    const encodedPayload = new TextEncoder().encode(data);
    const hash = await crypto.subtle.digest("SHA-256", encodedPayload);
    const hashArray = Array.from(new Uint8Array(hash));
    return hashArray.map((bytes) => bytes.toString(16).padStart(2, "0")).join("");
  }
}

export const httpClient = new HttpClient();
